import { Alert, Divider, Grid } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DimensionDescriptor } from "../../../../../../shared/reporting/api/biClient.types";
import useDeferredDictionaryLoading from "../../../../../../shared/reporting/components/viewer/common/hooks/useDeferredDictionaryLoading";
import { useBiCommonDataContext } from "../../../../../../shared/reporting/contexts/BiCommonDataContext";
import { MetricDrillDownInfo } from "../../../../../api/biApi.types";
import { FieldWithOrder } from "../../../../../hooks/FieldWithOrder";
import useDrillDown from "../../../../../hooks/useDrillDown";
import { selectDimensions, selectDimensionsStructure } from "../../../../../store/selectors/metaDataSelectors";
import { selectDrillDownDefaultDimensions } from "../../../../../store/slices/drillDownConfigurationSlice";
import { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import { isMetricDimension } from "../../../../../utilities/MetricDimensionNames";
import CollapseContainer from "../../../../common/CollapseContainer";
import useCommonReportHandlers from "../../hooks/useCommonReportHandlers";
import ColumnsOptions from "../columns/ColumnsOptions";
import ConditionsCollapsedHeader from "../ConditionsCollapsedHeader";
import ConditionsPanel from "../ConditionsPanel";
import DrillDownGridWrapper from "../DrillDownGridWrapper";
import RecordsCounter from "../RecordsCounter";
import ToolBar from "../ToolBar";

interface Props {
  configuration: DrillDownPage;
  info: MetricDrillDownInfo;
}

const PmPart = ({ configuration, info }: Props) => {
  const dimensions = useSelector(selectDimensions);
  const dimensionsStructure = useSelector(selectDimensionsStructure);
  const defaultDimensions = useSelector(selectDrillDownDefaultDimensions);

  const state = useDrillDown(info);
  const handlers = useCommonReportHandlers();

  const [showColumnsOptions, setShowColumnsOptions] = useState<boolean | undefined>();

  const { dataSetId } = useBiCommonDataContext();
  useDeferredDictionaryLoading(
    dataSetId,
    { values: state.conditions, updateItem: state.actions.updateCondition },
    handlers.onLoadDictionaries
  );

  useEffect(() => {
    const fields = getDefaultFields(defaultDimensions, dimensions, info.metricDimension);
    state.actions.changeSelectedFields(fields);
    const entryNoField = fields.find((f) => f.field.name === "EntryNo");
    if (entryNoField) {
      state.actions.changeFieldSorting([{ field: entryNoField.field, sortAsc: true, order: 0 }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions]);

  const hideColumnsOptions = useCallback(() => setShowColumnsOptions(false), []);

  return (
    <Grid container sx={{ position: "relative", display: "flex", flex: 1, flexDirection: "row", width: "100%" }}>
      <CollapseContainer collapseElement={<ConditionsCollapsedHeader conditionsCount={state.conditions.length} />}>
        <ConditionsPanel state={state} configuration={configuration} info={info} />
      </CollapseContainer>
      <Grid container sx={{ flex: 1, width: "unset", flexDirection: "column" }}>
        <Grid container sx={{ flex: 1, width: "unset", flexDirection: "column", pl: "1.3rem", py: 2, pr: 3, gap: 2 }}>
          <ToolBar state={state} onShowColumnsOptions={() => setShowColumnsOptions(true)} />
          {state.error && (
            <Alert sx={{ mb: ".5rem" }} severity="error">
              {state.error}
            </Alert>
          )}
          {configuration && info.conditions && info.conditions.length > 0 && (
            <DrillDownGridWrapper state={state} info={info} />
          )}
        </Grid>
        <Divider />
        <Grid container sx={{ backgroundColor: grey[50], width: "100%" }}>
          <RecordsCounter loading={false} skip={state.skip} take={state.take} totalCount={state.rowsTotal} />
        </Grid>
      </Grid>
      {showColumnsOptions !== undefined && (
        <ColumnsOptions
          dimensions={dimensions}
          selectedFields={state.selectedFields}
          sortedFields={state.sortedFields}
          groupStructure={dimensionsStructure}
          open={showColumnsOptions === true}
          withAllocation={false}
          onApplySelection={state.actions.changeSelectedFields}
          onApplySorting={state.actions.changeFieldSorting}
          onClose={hideColumnsOptions}
        />
      )}
    </Grid>
  );
};

const getDefaultFields = (defaultDimensions: string[], allFields: DimensionDescriptor[], metricDimension: string) => {
  return defaultDimensions
    .filter((name) => !isMetricDimension(name) || (isMetricDimension(name) && name === metricDimension))
    .map<FieldWithOrder | undefined>((name, index) => {
      const field = allFields.find((f) => f.name === name);
      if (field) {
        return { field, order: index, canBeRemoved: name !== "No" };
      }
      return undefined;
    })
    .filter((item): item is FieldWithOrder => !!item);
};

export default PmPart;
